import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  timeout: 25000,
});

// Slow endpoints (multi-coord forecasts + reverse-geocoding) get a longer client.
export const slowApiClient = axios.create({
  baseURL: API,
  timeout: 60000,
});

export async function searchPlaces(q) {
  // Phase 41c — pass the user's browser locale so non-Latin searches
  // ("छितकुल" / "தஞ்சாவூர்" / "東京") work via Nominatim's accept-language.
  const lang = (typeof navigator !== "undefined" && navigator.language) || "en";
  const res = await apiClient.get("/geocode", { params: { q, lang } });
  return res.data;
}

export async function reverseGeocode(lat, lon) {
  // Phase 41d — pass browser locale so auto-detect shows place names in the
  // user's native script (e.g. "मुंबई · महाराष्ट्र" for Hindi locale).
  const lang = (typeof navigator !== "undefined" && navigator.language) || "en";
  const res = await apiClient.get("/reverse-geocode", { params: { lat, lon, lang } });
  return res.data;
}

export async function getForecast(lat, lon) {
  const res = await apiClient.get("/forecast", { params: { lat, lon } });
  return res.data;
}

export async function getRadarFrames() {
  const res = await apiClient.get("/radar-frames");
  return res.data;
}

export async function getNowcastSummary(payload) {
  const res = await apiClient.post("/nowcast-summary", payload);
  return res.data;
}

export async function getModelComparison(lat, lon, hours = 12, models) {
  const params = { lat, lon, hours };
  if (models) params.models = models;
  const res = await apiClient.get("/model-comparison", { params });
  return res.data;
}

export async function getLightningStatus() {
  const res = await apiClient.get("/lightning-status");
  return res.data;
}

export async function getLightningStrikes(lat, lon, radius_km = 100, mins = 45) {
  const res = await apiClient.get("/lightning", { params: { lat, lon, radius_km, mins } });
  return res.data;
}

export async function getRainConfidence(lat, lon) {
  const res = await apiClient.get("/rain-confidence", { params: { lat, lon } });
  return res.data;
}

export async function getIndiaExtremes(type, limit = 10, { bustCache = false } = {}) {
  // When the user taps the masthead refresh button we pass `bustCache: true`
  // → adds a `_t` query param so the browser/service-worker treat it as a
  // distinct URL and skip any cached response (the API itself already
  // serves from its 15-min MongoDB snapshot, this just defeats the
  // browser-side and SW-side caching for that one request).
  const params = bustCache
    ? { type, limit, _t: Date.now() }
    : { type, limit };
  const res = await apiClient.get("/india-extremes", { params });
  return res.data;
}

export async function getHailRisk(lat, lon) {
  const res = await apiClient.get("/hail-risk", { params: { lat, lon } });
  return res.data;
}

export async function getAirQuality(lat, lon) {
  const res = await apiClient.get("/air-quality", { params: { lat, lon } });
  return res.data;
}

export async function getStormReplay(lat, lon) {
  const res = await apiClient.get("/storm-replay", { params: { lat, lon } });
  return res.data;
}

export async function getHitRate(lat, lon, days = 7) {
  const res = await apiClient.get("/forecast/hit-rate", { params: { lat, lon, days } });
  return res.data;
}

export async function getCityPulse(lat, lon, radius_km = 25, limit = 10) {
  const res = await apiClient.get("/city-pulse", {
    params: { lat, lon, radius_km, limit },
  });
  return res.data;
}

export async function getReportBundle(lat, lon) {
  const res = await slowApiClient.get("/report-bundle", { params: { lat, lon } });
  return res.data;
}

export async function getMarine(lat, lon) {
  const res = await apiClient.get("/marine", { params: { lat, lon } });
  return res.data;
}

export async function getNowcastOpticalFlow(lat, lon, minutes = 60) {
  const res = await apiClient.get("/nowcast/optical-flow", { params: { lat, lon, minutes } });
  return res.data;
}

// ------------------------------------------------------------------ Saved locations
export async function getSavedLocations() {
  const res = await apiClient.get("/saved-locations", { withCredentials: true });
  return res.data;
}

export async function syncSavedLocations(locations) {
  const res = await apiClient.post(
    "/saved-locations/sync",
    { locations },
    { withCredentials: true },
  );
  return res.data;
}

export async function setLocationAlerts(locationId, enabled) {
  const res = await apiClient.patch(
    `/saved-locations/${encodeURIComponent(locationId)}`,
    { alerts_enabled: !!enabled },
    { withCredentials: true },
  );
  return res.data;
}

export async function deleteSavedLocation(locationId) {
  const res = await apiClient.delete(
    `/saved-locations/${encodeURIComponent(locationId)}`,
    { withCredentials: true },
  );
  return res.data;
}

export async function pushTestNotification() {
  const res = await apiClient.post("/push/test", {}, { withCredentials: true });
  return res.data;
}

export async function getNotificationPrefs() {
  const res = await apiClient.get("/notification-prefs", { withCredentials: true });
  return res.data;
}

export async function putNotificationPrefs(prefs) {
  const res = await apiClient.put("/notification-prefs", prefs, {
    withCredentials: true,
  });
  return res.data;
}

export async function getAlertCategories() {
  const res = await apiClient.get("/alert-categories", { withCredentials: true });
  return res.data;
}

export async function putAlertCategories(patch) {
  const res = await apiClient.put("/alert-categories", patch, {
    withCredentials: true,
  });
  return res.data;
}
